/* eslint formatjs/no-literal-string-in-jsx: off -- internal tool intentionally not translated */
import { getContentfulClient } from "../../lib/contentful/contentfulClient"
import { reportErrorMessage } from "../../utils/ErrorReporting"
// eslint-disable-next-line no-restricted-imports -- FIXME: Usages of styled-components should be replaced with components from carotene-core or carotene-core-x. Please do not import styled-components after eslint rule added on 3/21/2025.
import styled from "styled-components"
import React, { FC, useEffect, useState } from "react"
import { getIsDynamicContentToggleToolOpen } from "#/redux/reducers/application"
import { toggleDymamicContentTogglesTool } from "#/redux/actions/testUserActions"
import { useDispatch, useSelector } from "react-redux"
import colors from "#/css/variables/_colors.module.scss"
import { RootState } from "#/redux/reducers"
// eslint-disable-next-line no-restricted-imports
import { Checkbox, FlexContainer, Select, SelectOptionType } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import useGetContentulCountries, { Country } from "./hooks/useContentfulCountries"
import { useIntl } from "react-intl"
import { useLocaleContext } from "#/components/context/user/LocaleContext"
import { useContentfulConditions } from "#/services/contentful/hooks/use-contentful-conditions"
import { useCurrentUser } from "../context/user/UserContext"
import { Locale } from "../../types/Locale"
import { localeToggleOptions } from "./localeToggleOptions"
import ContentTypes from "../../utils/ContentTypes"
import { TypeAttribute, TypeAttributeSkeleton } from "#/types/contentful"
import { Button } from "@carrotfertility/carotene-core"
import useGetMemberCountry, { useCreateCountryOverride } from "#/components/company/hooks/useGetMemberCountry"
import { CountryCode } from "#/utils/CountryCode"
import { useGetTextTokens } from "#/lib/contentful/hooks/useGetTextTokens"

type DynamicContentTogglesProps = unknown

const ToggleLabel = styled.label`
  inline-size: 15rem;
  cursor: pointer;
`
const ToggleDiv = styled.div`
  padding: 0.25rem 1rem 0.25rem 0.25rem;
  background-color: ${colors.lightGray};
  border-block-end: 0.1rem solid white;
`
const ToggleDescLink = styled.a`
  float: inline-end;
  font-size: 1rem;
  cursor: pointer;
`
const ToggleModal = styled.div`
  border: 0.15rem solid ${colors.mineralGrey};
  border-radius: 0.5rem;
  padding: 1rem;
  inline-size: 20rem;
  opacity: 0.85;
  position: fixed;
  inset-block-start: 1rem;
  inset-inline-start: 1rem;
  z-index: 20;
`
const TogglesDiv = styled.div`
  margin-block-start: 0.5rem;
  max-block-size: 35rem;
  overflow: auto;
  font-size: 1rem;
`
const SearchInput = styled.input`
  inline-size: 100%;
  padding: 0.5rem;
  border: 0.1rem solid ${colors.mineralGrey};
  border-radius: 0.25rem;
`

export const DynamicContentToggles: FC<DynamicContentTogglesProps> = () => {
  const currentUser = useCurrentUser()
  if (!currentUser?.isTestUser) {
    return null
  } else {
    return <RenderedDynamicContentToggles />
  }
}

type AttributeEntry = TypeAttribute<undefined>

const RenderedDynamicContentToggles: FC<DynamicContentTogglesProps> = () => {
  const [attributes, setAttributes] = useState<AttributeEntry[]>(null)
  const [showAsKeys, setShowAsKeys] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>("")
  const { data: countries, isLoading: isLoadingCountries } = useGetContentulCountries()
  const conditionMap = useContentfulConditions()
  const dispatch = useDispatch()

  const { isDynamicContentToggleToolOpen } = useSelector((state: RootState) => ({
    isDynamicContentToggleToolOpen: getIsDynamicContentToggleToolOpen(state)
  }))

  function toggleShowAsKeys(): void {
    setShowAsKeys(!showAsKeys)
  }

  const getAttributes = async () => {
    const client = getContentfulClient()
    const { items } = await client.getEntries<TypeAttributeSkeleton>({
      content_type: ContentTypes.ATTRIBUTE,
      limit: 200
    })
    return items
  }

  useEffect(() => {
    async function loadAttributes(): Promise<void> {
      const dynamicAttribs = await getAttributes()
      setAttributes(dynamicAttribs.filter(({ fields: { key } }) => typeof conditionMap[key] !== "undefined"))
      dynamicAttribs
        .filter(({ fields: { key } }) => typeof conditionMap[key] === "undefined")
        .forEach(({ fields: { key } }) => reportErrorMessage(`No mapping for Contentful attribute with key: ${key}`))
    }
    if (!attributes) {
      loadAttributes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  const sortByName = (
    { fields: { name: nameA } }: AttributeEntry,
    { fields: { name: nameB } }: AttributeEntry
  ): number => {
    if (nameA < nameB) return -1
    if (nameA > nameB) return 1
    return 0
  }
  const sortByKey = ({ fields: { key: keyA } }: AttributeEntry, { fields: { key: keyB } }: AttributeEntry): number => {
    if (keyA < keyB) return -1
    if (keyA > keyB) return 1
    return 0
  }

  if (attributes && !isLoadingCountries) {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase()
    const sortedAttributes = attributes.slice().sort(showAsKeys ? sortByKey : sortByName)
    const filteredAttributes = sortedAttributes.filter(({ fields: { name, description, key } }) => {
      if (!normalizedSearchQuery) {
        return true
      }
      const searchableText = `${name} ${key} ${description || ""}`.toLowerCase()
      return searchableText.includes(normalizedSearchQuery)
    })

    return isDynamicContentToggleToolOpen ? (
      <ToggleModal className={`bg-white  DynamicContentToggles`}>
        <button
          className={`fr pointer bw0 bg-white black-60 hover-black animate-all`}
          onClick={() => dispatch(toggleDymamicContentTogglesTool())}
          style={{ lineHeight: 0 }}
        >
          <img alt="close" src="/images/icn-close-modal.svg" />
        </button>
        <Button variant="text" color="secondary" onClick={toggleShowAsKeys}>
          {showAsKeys ? "Display as names" : "Display as keys"}
        </Button>
        <TogglesDiv className={`flex flex-column overflow-y-auto br3`}>
          <ToggleDiv>
            <SearchInput
              type="search"
              placeholder="Search toggles by name, key, or description"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </ToggleDiv>
          <CountryToggle countries={countries} />
          <LocaleToggle />
          <TokensPanel />
          {attributes.length
            ? filteredAttributes.length
              ? filteredAttributes.map((attrib) => (
                  <AttribDisplay
                    attribute={attrib}
                    showAsKeys={showAsKeys}
                    key={`test_${attrib.fields.key}`}
                    onChange={() => {
                      conditionMap[attrib.fields.key].toggle()
                    }}
                    isOn={conditionMap[attrib.fields.key]?.value}
                  />
                ))
              : "No matching toggles"
            : "No Toggles Available"}
        </TogglesDiv>
      </ToggleModal>
    ) : null
  } else {
    return null
  }
}

const sortCountry = ({ name: nameA }: Country, { name: nameB }: Country): number => {
  if (nameA < nameB) return -1
  if (nameA > nameB) return 1
  return 0
}

const CountryToggle = ({ countries }: { countries: Country[] }): JSX.Element => {
  const memberCountry = useGetMemberCountry()
  const countryCode = memberCountry?.data?.countryCode
  const createCountryOverride = useCreateCountryOverride()

  const options = countries.sort(sortCountry).map(({ name, code }) => ({ label: name, value: code }))
  const currentValue = options.find(({ value }) => value === countryCode)

  const updateCountryCode = ({ value }: SelectOptionType): void => {
    createCountryOverride({ countryCode: value as CountryCode })
  }

  return (
    <ToggleDiv>
      <div>
        <ToggleLabel>Company Country</ToggleLabel>
        <Select options={options} value={currentValue} onChange={updateCountryCode} />
      </div>
    </ToggleDiv>
  )
}

const LocaleToggle = (): JSX.Element => {
  const { locale: localeString } = useIntl()
  const { setLocale } = useLocaleContext()

  const currentValue = localeToggleOptions.find(({ value }) => value === localeString)

  const updateLocale = ({ value }: SelectOptionType): void => {
    if (typeof value === "string") {
      setLocale(Locale.getLocaleFromString(value))
    }
  }

  return (
    <ToggleDiv>
      <div>
        <ToggleLabel>Locale</ToggleLabel>
        <Select options={localeToggleOptions} value={currentValue} onChange={updateLocale} />
      </div>
    </ToggleDiv>
  )
}

const TokensPanel = (): JSX.Element => {
  const { data: tokens, isLoading } = useGetTextTokens()
  const [tokenSearchQuery, setTokenSearchQuery] = useState<string>("")
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  if (isLoading || !tokens || tokens.length === 0) return null

  const normalizedQuery = tokenSearchQuery.trim().toLowerCase()
  const filtered = tokens
    .filter((token) => token.fields)
    .filter((token) => {
      if (!normalizedQuery) return true
      const name = token.fields.name ?? ""
      const value = token.fields.value ?? ""
      return `${name} ${value}`.toLowerCase().includes(normalizedQuery)
    })
    .sort((a, b) => {
      const nameA = a.fields.name ?? ""
      const nameB = b.fields.name ?? ""
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0
    })

  return (
    <ToggleDiv>
      <Button variant="text" color="secondary" onClick={() => setIsExpanded(!isExpanded)}>
        {isExpanded ? "Hide text tokens" : `Show text tokens (${tokens.length})`}
      </Button>
      {isExpanded && (
        <>
          <SearchInput
            type="search"
            placeholder="Search tokens by name or value"
            value={tokenSearchQuery}
            onChange={(event) => setTokenSearchQuery(event.target.value)}
            style={{ marginBlockStart: "0.5rem" }}
          />
          <div style={{ marginBlockStart: "0.5rem", fontSize: "0.85rem" }}>
            {filtered.length === 0 ? (
              <span>No matching tokens</span>
            ) : (
              filtered.map((token) => {
                const name = token.fields.name
                const value = token.fields.value
                const hasCondition = (token.fields.conditions?.length ?? 0) > 0
                return (
                  <div key={token.sys.id} style={{ paddingBlock: "0.25rem", borderBlockEnd: "1px solid #eee" }}>
                    <code>[[{name}]]</code> → <span>{value}</span>
                    {hasCondition && <span style={{ marginInlineStart: "0.5rem", opacity: 0.7 }}>(conditional)</span>}
                  </div>
                )
              })
            )}
          </div>
        </>
      )}
    </ToggleDiv>
  )
}

type DynamicAttributeProps = {
  attribute: AttributeEntry
  showAsKeys: boolean
  onChange: (args: unknown) => unknown
  isOn: boolean
}
const AttribDisplay: FC<DynamicAttributeProps> = ({
  attribute: {
    fields: { name, description, key }
  },
  showAsKeys,
  onChange,
  isOn
}) => {
  const [showDescription, setShowDescription] = useState<boolean>(false)
  function toggleDescription(): void {
    setShowDescription(!showDescription)
  }

  const shouldDisableCheckbox = (key: string) =>
    ["has_carrot_plan", "elective_fertility_qme_line_item_filter", "is_non_english_locale_selected"].includes(key)

  return (
    <ToggleDiv>
      <FlexContainer alignItems="center">
        <ToggleLabel onClick={onChange}>
          <Checkbox isDisabled={shouldDisableCheckbox(key)} isChecked={isOn} value={key}>
            {showAsKeys ? key : name}
          </Checkbox>
        </ToggleLabel>
        {description && (
          <ToggleDescLink onClick={toggleDescription}>
            <img
              className={`animate-all vmid ${showDescription ? "rotate-180" : ""}`}
              alt={showDescription ? "close" : "open"}
              src={`/images/icn-upwards-arrow.png`}
              style={{ minWidth: "17px" }}
            />
          </ToggleDescLink>
        )}
      </FlexContainer>
      {showDescription && <span>{description}</span>}
    </ToggleDiv>
  )
}
